import React, { useMemo, useState, useEffect, useRef } from 'react'
import { times } from 'lodash'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import { Document, Page, Thumbnail } from 'react-pdf'
import { LinearProgress } from '@mui/material'
import PerfectScrollbar from 'react-perfect-scrollbar'

import lang from '@/lang'
import { user } from 'ampersand-app'
import ThemeInterface from '@/theme-interface'
import IconZoomMinus from '@assets/images/icons/zoom_minus.svg'
import IconZoomPlus from '@assets/images/icons/zoom_plus.svg'
import IconTrash from '@assets/images/icons/trashcan.svg'
import IconSigningPen from '@assets/images/icons/signature_pen.svg'
import { DocumentControlButton } from '@/modules/base/components/buttons'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import SignaturePositionOverlay from '@/modules/signature/components/overlay/signature-position-overlay'
import SignaturePager from '@/modules/signature/components/pager/signature-pager'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { useAppSelector } from '@/redux/store'
import { SignatureWizardSteps } from '@/redux/ducks/signature/edit'
import { Document as File, UploaderFile } from '@/modules/document/types'
import FileService from '@/modules/file/fileService'
import axios from 'axios'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { mimeTypes } from '@/modules/base/util/constants'
import Loader from '@/modules/base/components/loader/loader'
import getCollaborationRequestHeader from '@/modules/document/_helpers/getCollaborationRequestHeader'

type DocumentOptions = {
  httpHeaders?: { authorization: string }
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'grid',
    gridTemplateAreas: '"leftArea rightArea"',
    gridTemplateColumns: '3fr 11fr',
    height: '100%',
  },
  document: {
    padding: '15px 20px',
  },
  leftArea: {
    gridArea: 'leftArea',
    display: 'grid',
    gridTemplateAreas: '"actions thumbnails"',
    gridTemplateColumns: 'min-content max-content',
    paddingRight: 40,
    overflow: 'hidden',
  },
  actions: {
    gridArea: 'actions',
    display: 'flex',
    flexDirection: 'column',
    padding: '30px 15px',
    backgroundColor: theme.backgroundColor,
  },
  pager: {
    height: 'unset',
    fontSize: theme.fontSizes.smaller,
    justifyContent: 'center',
    marginBottom: 25,
  },
  thumbnails: {
    gridArea: 'thumbnails',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    marginTop: 30,
  },
  thumbnailWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    padding: '5px 30px',
  },
  thumbnail: {
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    border: '1px solid transparent',
    position: 'relative',
  },
  thumbnailLabel: {
    fontSize: theme.fontSizes.smaller,
    marginTop: 12,
  },
  signatureBadge: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    background: theme.primary,
  },
  active: {
    borderColor: theme.borderColors.outline,
  },
  rightArea: {
    gridArea: 'rightArea',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 30,
  },
  rightAreaStart: {
    justifyContent: 'flex-start',
    paddingRight: 0,
    paddingLeft: 0,
  },
  page: {
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    border: `1px solid ${theme.borderColors.default}`,
  },
  scrollbarRightArea: {
    '& .ps__rail-x, & .ps__rail-y': {
      zIndex: 10,
    },
  },
}), { name: 'DocumentPreview' })

interface SignatureDocumentPreviewProps {
  file: File,
  auth?: boolean,
  hideDelete?: boolean,
  onRemoveFile?: () => void
}

const SignatureDocumentPreview = (props: SignatureDocumentPreviewProps) => {
  const classes = useStyles()
  const theme: ThemeInterface = useTheme()
  const { file, auth, hideDelete, onRemoveFile } = props
  const [pageCount, setPageCount] = useState<number>(1)
  const [scale, setScale] = useState<number>(1)
  const [pdf, setPdf] = useState(null)
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const rightAreaRef = useRef<HTMLDivElement>(null)

  const dmsFileUrl = useAppSelector((state) => state.tenant.settings.dmsFileUrl)

  const isPdf = file?.mimetype === mimeTypes.MIMETYPE_PDF || file?.file?.type === mimeTypes.MIMETYPE_PDF

  const {
    pager: {
      currentPage: currentPagerPage,
      setNumPages: setContextPagerNumPages,
      setCurrentPage,
    },
    resetContext,
    setPageSize: setContextPageSize,
    items,
  } = useSignatureContext()

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (rightAreaRef.current.scrollWidth > rightAreaRef.current.clientWidth) {
        rightAreaRef.current.classList.add(classes.rightAreaStart)
      } else {
        rightAreaRef.current.classList.remove(classes.rightAreaStart)
      }
    })

    resizeObserver.observe(rightAreaRef.current)

    return () => {
      resizeObserver.disconnect()
      resetContext()
    }
  }, [])

  const catchError = ({ response }) => {
    setError(true)
    response.data.text().then(e => {
      if (JSON.parse(e).name === 'DmsError') {
        toast.showToast({ type: 'error', message: lang.dmsError })
      }
    })
  }

  useEffect(() => {
    setPageCount(0) // workaround for react-pdf issue #974
    setData(null)
    setError(false)

    if (!file?.id && file?.data) {
      return setData(file.data)
    }

    if (file?.minioId && dmsFileUrl) {
      FileService.streamDmsFile(file.minioId)
        .then(setData)
        .catch(catchError)
    } else if (isPdf) {
      axios.get(file.url, { headers: options.httpHeaders, responseType: 'blob' })
        .then(async({ data }) => setData(URL.createObjectURL(data)))
        .catch(catchError)
    }
  }, [file?.id])

  const options: DocumentOptions = useMemo(() => {
    return auth ? { httpHeaders: { 'authorization': `Bearer ${user.token}`, ...getCollaborationRequestHeader() } } : {}
  }, [auth])

  const currentPage = currentPagerPage

  const onLoadSuccess = (pdf): void => {
    setPdf(pdf)
    setPageCount(pdf.numPages)
    setContextPagerNumPages(pdf.numPages)
  }
  const onThumbnailClick = ({ pageNumber }: { pageIndex: number, pageNumber: number }): void => {
    setCurrentPage(pageNumber)
  }
  const handlePageLoadSuccess = (page) => {
    setContextPageSize({ width: page.width, height: page.height, originalHeight: page.originalHeight, originalWidth: page.originalWidth })
  }

  const thumbnails = useMemo(() => {
    if (!pdf || !isPdf || (dmsFileUrl && !data)) { return null }
    return times(pageCount, (index) => {
      const pageNumber = index + 1
      const hasSignature = items.find(({ position, signed }) => (position.page === pageNumber && !signed))
      return (
        <div key={index} className={classes.thumbnailWrapper}>
          <Thumbnail
            pdf={pdf}
            pageNumber={pageNumber}
            height={195}
            width={122}
            className={clsx(classes.thumbnail, currentPage === pageNumber && classes.active)}
            onItemClick={onThumbnailClick}>
            {hasSignature && (
              <div className={classes.signatureBadge}>
                <SVGIcon svg={IconSigningPen} height="10px" width="10px" fill={theme.white} />
              </div>
            )}
          </Thumbnail>
          <span className={classes.thumbnailLabel}>{`Seite ${pageNumber}`}</span>
        </div>
      )
    })
  }, [pageCount, currentPage, pdf, items])

  if (!file) { return null }

  return (
    <div className={classes.root}>
      <div className={classes.leftArea}>
        <div className={classes.actions}>
          <DocumentControlButton
            icon={IconZoomPlus}
            debounceTime={0}
            title={lang.expense.previewToolbar.items.zoomIn}
            onClick={() => {
              setScale(scale + 0.1)
            }} />
          <DocumentControlButton
            icon={IconZoomMinus}
            debounceTime={0}
            title={lang.expense.previewToolbar.items.zoomOut}
            onClick={() => {
              const newScale = scale - 0.1
              if (newScale <= 0.5) {
                return
              }
              setScale(newScale)
            }} />
          {!hideDelete && <DocumentControlButton
            title={lang.expense.previewToolbar.items.delete}
            icon={IconTrash}
            onClick={onRemoveFile} />
          }
        </div>
        <div className={classes.thumbnails}>
          <SignaturePager customClass={classes.pager} />
          <PerfectScrollbar>
            {thumbnails}
          </PerfectScrollbar>
        </div>
      </div>
      <PerfectScrollbar className={classes.scrollbarRightArea}>
        <div ref={rightAreaRef} className={classes.rightArea}>
          {dmsFileUrl && file.minioId && !data && !error
            ? <Loader text={lang.documents.loadingFile} />
            : <Document
              file={data}
              options={options}
              className={classes.document}
              renderMode='canvas'
              loading={lang.pdfLoading}
              onLoadSuccess={onLoadSuccess}>
              <SignaturePositionOverlay>
                <Page
                  className={classes.page}
                  pageNumber={currentPage}
                  scale={scale}
                  loading={<LinearProgress variant="indeterminate" />}
                  onLoadSuccess={handlePageLoadSuccess} />
              </SignaturePositionOverlay>
            </Document>}
        </div>
      </PerfectScrollbar>
    </div>
  )
}

export default SignatureDocumentPreview
